/**
 * Tabela padrão do layout A + C e a barra de seleção.
 *
 * `<DataTable label rows columns rowKey ... />`
 * - `columns`: `{ id, header, cell(row), sortValue?(row), align?, mono?, width?, nowrap? }[]` — com `sortValue` o
 *   cabeçalho vira botão de ordenar (`aria-sort` no th);
 * - ordenação: sem controle, a tabela ordena sozinha a partir de `defaultSort`; controlada (`sort` + `onSortChange`),
 *   ela só avisa e quem chama manda as linhas já ordenadas (ordenação no servidor);
 * - seleção: `selected` (chaves) + `onSelectedChange` liga a coluna de caixas, com "selecionar todos" (o que está
 *   visível) e estado misto;
 * - linha destacada: `activeKey` (o detalhe aberto ao lado) e `onRowClick(row)` — clique, Enter ou espaço na linha;
 *   cliques em links, botões e caixas dentro da linha não contam;
 * - `rowActions(row)`: slot de ações no fim da linha (botões pequenos);
 * - paginação: `pageSize` mostra aos poucos ("Carregar mais", padrão) ou em páginas (`paging="pages"`); no servidor,
 *   `hasMore` + `onLoadMore` (e `total` para o "Mostrando N de M");
 * - uma rolagem só: a tabela cresce com a página e o cabeçalho fica preso no topo dela (logo abaixo da `ListToolbar`);
 *   tabela mais larga que o quadro ganha rolagem de lado (aí o cabeçalho volta a rolar junto). `maxHeight` só para
 *   tabelas pequenas embutidas (dentro de um cartão ou da gaveta), nunca para a lista principal da tela;
 * - `empty`: o que mostrar sem linhas; `framed={false}` tira a borda (dentro de um Panel).
 *
 * `<SelectionBar count actions onClear />` — barra escura "N selecionados" com as ações e "Limpar seleção"; some com 0.
 */
import { useI18n, type CommonKey } from '@hive/i18n';
import { useEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react';
import { cx } from './layout';

export interface DataColumn<T> {
  id: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  /** Valor para ordenar; sem ele, a coluna não ordena. */
  sortValue?: (row: T) => string | number | null | undefined;
  align?: 'left' | 'right' | 'center';
  mono?: boolean;
  width?: number | string;
  /** Rótulo para leitores de tela quando o cabeçalho é ícone ou vazio. */
  srHeader?: string;
}

export type SortState = { id: string; dir: 'asc' | 'desc' };

export function DataTable<T>({
  label,
  rows,
  columns,
  rowKey,
  defaultSort,
  sort: sortProp,
  onSortChange,
  selected,
  onSelectedChange,
  rowLabel,
  activeKey,
  onRowClick,
  rowActions,
  actionsHeader,
  pageSize,
  paging = 'more',
  hasMore,
  onLoadMore,
  total,
  maxHeight = null,
  empty,
  framed = true,
  className,
}: {
  /** Nome acessível da tabela. */
  label: string;
  rows: T[];
  columns: DataColumn<T>[];
  rowKey: (row: T) => string;
  defaultSort?: SortState;
  sort?: SortState | null;
  /** `add`: Shift+clique (quem ordena no servidor pode somar uma segunda coluna). */
  onSortChange?: (s: SortState, opts?: { add: boolean }) => void;
  selected?: string[];
  onSelectedChange?: (keys: string[]) => void;
  /** Nome da linha na caixa de seleção ("Selecionar CITDFM5412"); padrão: a chave. */
  rowLabel?: (row: T) => string;
  activeKey?: string | null;
  onRowClick?: (row: T) => void;
  rowActions?: (row: T) => ReactNode;
  actionsHeader?: string;
  pageSize?: number;
  paging?: 'more' | 'pages';
  hasMore?: boolean;
  onLoadMore?: () => void;
  total?: number;
  maxHeight?: number | string | null;
  empty?: ReactNode;
  framed?: boolean;
  className?: string;
}) {
  const { t, fmt } = useI18n<CommonKey>();
  const controlled = sortProp !== undefined;
  const [ownSort, setOwnSort] = useState<SortState | null>(defaultSort ?? null);
  const sort = controlled ? sortProp : ownSort;
  const [shown, setShown] = useState(pageSize ?? Infinity);
  const [page, setPage] = useState(0);
  // lista nova (outro filtro): volta ao começo; a mesma lista desenhada de novo (seleção, detalhe) não mexe na página
  const listSig = `${rows.length}:${rows.length ? rowKey(rows[0] as T) : ''}`;
  useEffect(() => {
    setShown(pageSize ?? Infinity);
    setPage(0);
  }, [listSig, pageSize]);

  const sorted = useMemo(() => {
    if (controlled || !sort) return rows;
    const col = columns.find((c) => c.id === sort.id);
    if (!col?.sortValue) return rows;
    const val = col.sortValue;
    const k = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const x = val(a);
      const y = val(b);
      if (x == null && y == null) return 0;
      if (x == null) return 1;
      if (y == null) return -1;
      if (typeof x === 'number' && typeof y === 'number') return (x - y) * k;
      return String(x).localeCompare(String(y), undefined, { numeric: true, sensitivity: 'base' }) * k;
    });
  }, [rows, columns, sort, controlled]);

  const pages = pageSize && paging === 'pages' ? Math.max(1, Math.ceil(sorted.length / pageSize)) : 1;
  const visible = pageSize ? (paging === 'pages' ? sorted.slice(page * pageSize, (page + 1) * pageSize) : sorted.slice(0, shown)) : sorted;
  const sel = new Set(selected ?? []);
  const selectable = !!onSelectedChange;
  const visKeys = visible.map(rowKey);
  const allOn = visKeys.length > 0 && visKeys.every((k) => sel.has(k));
  const someOn = visKeys.some((k) => sel.has(k));
  const allRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (allRef.current) allRef.current.indeterminate = someOn && !allOn;
  }, [someOn, allOn]);

  const toggleSort = (c: DataColumn<T>, add = false) => {
    const next: SortState = sort?.id === c.id ? { id: c.id, dir: sort.dir === 'asc' ? 'desc' : 'asc' } : { id: c.id, dir: 'asc' };
    if (!controlled) setOwnSort(next);
    onSortChange?.(next, { add });
  };
  const setAll = (on: boolean) => {
    const rest = (selected ?? []).filter((k) => !visKeys.includes(k));
    onSelectedChange?.(on ? [...rest, ...visKeys] : rest);
  };
  const setOne = (k: string, on: boolean) => onSelectedChange?.(on ? [...(selected ?? []).filter((x) => x !== k), k] : (selected ?? []).filter((x) => x !== k));
  const fromControl = (e: MouseEvent | KeyboardEvent) => !!(e.target as HTMLElement).closest('a, button, input, select, textarea, label, [role="button"]');
  const style: CSSProperties | undefined = maxHeight == null ? undefined : { maxHeight };
  const remaining = sorted.length - visible.length;
  // mais larga que o quadro: rolagem de lado (sem ela, o quadro não corta nada e o cabeçalho prende no topo da página)
  const frame = useRef<HTMLDivElement>(null);
  const [wide, setWide] = useState(false);
  useEffect(() => {
    const el = frame.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const check = () => {
      const table = el.querySelector('table');
      setWide(!!table && table.scrollWidth > el.clientWidth + 1);
    };
    const ro = new ResizeObserver(check);
    ro.observe(el);
    const table = el.querySelector('table');
    if (table) ro.observe(table);
    check();
    return () => ro.disconnect();
  }, []);

  return (
    <div className={cx('dt', className)}>
      <div ref={frame} className={cx('dt-frame', framed && 'framed', maxHeight != null && 'dt-scroll', maxHeight == null && wide && 'dt-wide')} style={style}>
        <table className="tbl dt-table" aria-label={label}>
          <thead>
            <tr>
              {selectable && (
                <th scope="col" className="cb">
                  <input ref={allRef} type="checkbox" aria-label={t('ui.table.selectAll')} checked={allOn} onChange={(e) => setAll(e.target.checked)} disabled={!visKeys.length} />
                </th>
              )}
              {columns.map((c) => {
                const on = sort?.id === c.id;
                return (
                  <th
                    key={c.id}
                    scope="col"
                    className={cx(c.align === 'right' && 'num', c.align === 'center' && 'center')}
                    style={c.width != null ? { width: c.width } : undefined}
                    aria-sort={c.sortValue || (controlled && onSortChange) ? (on ? (sort?.dir === 'asc' ? 'ascending' : 'descending') : 'none') : undefined}
                  >
                    {c.sortValue || (controlled && onSortChange) ? (
                      <button type="button" className={cx('dt-sort', on && 'on')} onClick={(e) => toggleSort(c, e.shiftKey)}>
                        {c.header || <span className="sr-only">{c.srHeader}</span>}
                        <span className="dt-arrow" aria-hidden="true">
                          {on ? (sort?.dir === 'asc' ? '▲' : '▼') : '↕'}
                        </span>
                      </button>
                    ) : (
                      c.header || <span className="sr-only">{c.srHeader}</span>
                    )}
                  </th>
                );
              })}
              {rowActions && (
                <th scope="col" className="actions">
                  <span className="sr-only">{actionsHeader ?? t('ui.table.actions')}</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {visible.map((r) => {
              const k = rowKey(r);
              const active = activeKey != null && k === activeKey;
              const checked = sel.has(k);
              return (
                // a linha abre o detalhe pelo mouse e pelo teclado (Enter/espaço); os controles dentro dela continuam sendo os seus
                <tr
                  key={k}
                  className={cx(active && 'sel', checked && 'checked', onRowClick && 'clickable')}
                  aria-current={active ? 'true' : undefined}
                  tabIndex={onRowClick ? 0 : undefined}
                  onClick={onRowClick ? (e) => !fromControl(e) && onRowClick(r) : undefined}
                  onKeyDown={
                    onRowClick
                      ? (e) => {
                          if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
                            e.preventDefault();
                            onRowClick(r);
                          }
                        }
                      : undefined
                  }
                >
                  {selectable && (
                    <td className="cb">
                      <input type="checkbox" aria-label={t('ui.table.selectRow', { name: rowLabel ? rowLabel(r) : k })} checked={checked} onChange={(e) => setOne(k, e.target.checked)} />
                    </td>
                  )}
                  {columns.map((c) => (
                    <td key={c.id} className={cx(c.align === 'right' && 'num', c.align === 'center' && 'center', c.mono && 'mono')}>
                      {c.cell(r)}
                    </td>
                  ))}
                  {rowActions && <td className="actions">{rowActions(r)}</td>}
                </tr>
              );
            })}
          </tbody>
        </table>
        {!visible.length && <div className="dt-empty">{empty ?? t('ui.table.empty')}</div>}
      </div>
      {(remaining > 0 || pages > 1 || hasMore || total != null) && (
        <div className="dt-foot">
          <span>{t('ui.table.showing', { shown: fmt.number(visible.length + (paging === 'pages' ? page * (pageSize ?? 0) : 0)), total: fmt.number(total ?? sorted.length) })}</span>
          {paging === 'pages' && pages > 1 ? (
            <span className="row">
              <button type="button" className="btn btn-sm" disabled={page === 0} onClick={() => setPage(page - 1)}>
                {t('ui.table.prev')}
              </button>
              <span>{t('ui.table.page', { page: page + 1, pages })}</span>
              <button type="button" className="btn btn-sm" disabled={page >= pages - 1} onClick={() => setPage(page + 1)}>
                {t('ui.table.next')}
              </button>
            </span>
          ) : remaining > 0 ? (
            <button type="button" className="btn btn-sm" onClick={() => setShown((n) => n + (pageSize ?? 50))}>
              {t('ui.table.loadMore', { n: fmt.number(remaining) })}
            </button>
          ) : hasMore && onLoadMore ? (
            <button type="button" className="btn btn-sm" onClick={onLoadMore}>
              {total != null ? t('ui.table.loadMore', { n: fmt.number(Math.max(0, total - rows.length)) }) : t('ui.table.more')}
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}

export function SelectionBar({ count, actions, onClear, className }: { count: number; actions?: ReactNode; onClear: () => void; className?: string }) {
  const { t } = useI18n<CommonKey>();
  if (count <= 0) return null;
  return (
    <div className={cx('selbar', className)} role="region" aria-label={t('ui.sel.count', { count })}>
      <b aria-live="polite">{t('ui.sel.count', { count })}</b>
      {actions}
      <button type="button" className="selbar-clear" onClick={onClear}>
        {t('ui.sel.clear')}
      </button>
    </div>
  );
}
